import React, { useState } from 'react';
import { 
  MessageSquare, 
  Send, 
  Image, 
  FileText, 
  Phone,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { whatsappService } from '../services/whatsappService';
import toast from 'react-hot-toast';

const MessagesPanel = () => {
  const [activeTab, setActiveTab] = useState('text');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Função para formatar o número do telefone
  const formatPhoneNumber = (phone) => {
    // Remove todos os caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Se já tem código do país (55), retorna como está
    if (cleanPhone.startsWith('55')) {
      return cleanPhone;
    }
    
    // Se não tem código do país, adiciona 55 (Brasil)
    if (cleanPhone.length === 11) { // DDD + número (11 dígitos)
      return `55${cleanPhone}`;
    }
    
    // Se tem menos de 11 dígitos, assume que é só o número
    return `55${cleanPhone}`;
  };

  const handleSendText = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber || !message) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setIsLoading(true);
    
    try {
      console.log('🔍 Debug - Enviando mensagem:', { phoneNumber, message });
      console.log('🔍 Debug - Token no localStorage:', localStorage.getItem('token'));
      
      const formattedPhone = formatPhoneNumber(phoneNumber);
      console.log('🔍 Debug - Número formatado:', formattedPhone);
      
      const result = await whatsappService.sendTextMessage(formattedPhone, message);
      console.log('🔍 Debug - Resultado:', result);
      
      if (result.success) {
        toast.success('Mensagem enviada com sucesso!');
        setLastResult(result);
        setMessage('');
      } else {
        toast.error('Erro ao enviar mensagem');
      }
    } catch (error) {
      console.error('🔍 Debug - Erro completo:', error);
      console.error('🔍 Debug - Response data:', error.response?.data);
      console.error('🔍 Debug - Status:', error.response?.status);
      toast.error('Erro ao enviar mensagem');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMedia = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber || !mediaUrl) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setIsLoading(true);
    
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      const result = await whatsappService.sendMedia(formattedPhone, mediaUrl, caption);
      if (result.success) {
        toast.success('Mídia enviada com sucesso!');
        setLastResult(result);
        setMediaUrl('');
        setCaption('');
      } else {
        toast.error('Erro ao enviar mídia');
      }
    } catch (error) {
      toast.error('Erro ao enviar mídia');
      console.error('Erro:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckNumber = async () => {
    if (!phoneNumber) {
      toast.error('Por favor, insira um número de telefone');
      return;
    }

    setIsLoading(true);
    
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      const result = await whatsappService.checkNumber(formattedPhone);
      if (result.success) {
        if (result.exists) {
          toast.success(`Número ${phoneNumber} está registrado no WhatsApp`);
        } else {
          toast.error(`Número ${phoneNumber} não está registrado no WhatsApp`);
        }
      } else {
        toast.error('Erro ao verificar número');
      }
    } catch (error) {
      toast.error('Erro ao verificar número');
      console.error('Erro:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'text', name: 'Mensagem de Texto', icon: FileText },
    { id: 'media', name: 'Mídia', icon: Image },
    { id: 'check', name: 'Verificar Número', icon: Phone }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Enviar Mensagens</h1>
        <p className="mt-2 text-gray-600">
          Envie mensagens de texto, mídia e verifique números
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Formulário de número */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Número de Destino</h3>
        <div className="flex space-x-4">
          <div className="flex-1">
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
              Número do Telefone
            </label>
            <input
              type="text"
              id="phoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Ex: 5517981386877"
              className="input-field"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleCheckNumber}
              disabled={isLoading || !phoneNumber}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Phone className="h-4 w-4 mr-2" />
              Verificar
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Digite o número com código do país (ex: 55 para Brasil) e DDD
        </p>
      </div>

      {/* Tab de mensagem de texto */}
      {activeTab === 'text' && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Mensagem de Texto</h3>
          <form onSubmit={handleSendText}>
            <div className="mb-4">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem
              </label>
              <textarea
                id="message"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Digite sua mensagem aqui..."
                className="input-field"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || !phoneNumber || !message}
              className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Enviar Mensagem
            </button>
          </form>
        </div>
      )}

      {/* Tab de mídia */}
      {activeTab === 'media' && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Enviar Mídia</h3>
          <form onSubmit={handleSendMedia}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="mediaUrl" className="block text-sm font-medium text-gray-700 mb-2">
                  URL da Mídia
                </label>
                <input
                  type="url"
                  id="mediaUrl"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label htmlFor="caption" className="block text-sm font-medium text-gray-700 mb-2">
                  Legenda (opcional)
                </label>
                <input
                  type="text"
                  id="caption"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Legenda da mídia"
                  className="input-field"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading || !phoneNumber || !mediaUrl}
              className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Image className="h-4 w-4 mr-2" />
              )}
              Enviar Mídia
            </button>
          </form>
        </div>
      )}

      {/* Tab de verificar número */}
      {activeTab === 'check' && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Verificar Número</h3>
          <p className="text-gray-600 mb-4">
            Verifique se um número está registrado no WhatsApp antes de enviar mensagens.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Como usar:</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li>Digite o número no campo acima</li>
              <li>Clique em "Verificar"</li>
              <li>O sistema informará se o número está ativo no WhatsApp</li>
            </ol>
          </div>
        </div>
      )}

      {/* Resultado da última operação */}
      {lastResult && (
        <div className="card bg-green-50 border-green-200">
          <h3 className="text-lg font-medium text-green-900 mb-4">Última Operação</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-green-700">Status</p>
              <p className="text-sm font-medium text-green-900">
                {lastResult.success ? 'Sucesso' : 'Erro'}
              </p>
            </div>
            <div>
              <p className="text-sm text-green-700">ID da Mensagem</p>
              <p className="text-sm font-medium text-green-900">
                {lastResult.messageId || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-green-700">Chat ID</p>
              <p className="text-sm font-medium text-green-900">
                {lastResult.chatId || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-green-700">Timestamp</p>
              <p className="text-sm font-medium text-green-900">
                {lastResult.timestamp ? new Date(lastResult.timestamp * 1000).toLocaleString('pt-BR') : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPanel;
